import { useCallback, useEffect, useRef, useState } from 'react';
import {
  supplierPerformanceAPI,
  type SupplierPerformanceDashboard,
  type SupplierPerformanceParams,
} from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useView } from '../../../contexts/ViewContext';

export type PeriodPreset = '12' | '24' | '36' | 'all' | 'custom';

export interface PerformanceFilterState {
  preset: PeriodPreset;
  /** YYYY-MM, used when preset is custom */
  from: string;
  /** YYYY-MM, used when preset is custom */
  to: string;
  /** '' = all divisions, a company id, or 'unassigned' */
  division: string;
  /** '' = all suppliers, or a company id */
  supplier: string;
}

export const DEFAULT_PERFORMANCE_FILTERS: PerformanceFilterState = {
  preset: '12',
  from: '',
  to: '',
  division: '',
  supplier: '',
};

/** Month arithmetic on YYYY-MM strings. */
export function shiftMonth(yyyyMm: string, delta: number): string {
  const [year, month] = yyyyMm.split('-').map(Number);
  const total = year * 12 + (month - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** The switcher's selection as request params, dropping unset levels. */
export function viewParams(raw: Record<string, number | undefined>): Pick<SupplierPerformanceParams, 'group' | 'company' | 'site'> {
  const params: Pick<SupplierPerformanceParams, 'group' | 'company' | 'site'> = {};
  if (raw['group']) params.group = raw['group'];
  if (raw['company']) params.company = raw['company'];
  if (raw['site']) params.site = raw['site'];
  return params;
}

function toParams(filters: PerformanceFilterState, latest: string | null, first: string | null): SupplierPerformanceParams {
  const params: SupplierPerformanceParams = {};
  if (filters.division) params.division = filters.division;
  if (filters.supplier) params.supplier = filters.supplier;
  if (filters.preset === 'custom') {
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
  } else if (filters.preset === 'all' && latest && first) {
    params.from = first;
    params.to = latest;
  } else if ((filters.preset === '24' || filters.preset === '36') && latest) {
    params.to = latest;
    params.from = shiftMonth(latest, 1 - Number(filters.preset));
  }
  // '12' sends no range: the server defaults to the twelve months to the latest data.
  return params;
}

/**
 * Loads the supplier performance dashboard for the current filters, scoped to
 * the group, region or division picked in the top view switcher.
 *
 * Keeps the last good response on screen while a new one loads, so changing a
 * filter does not blank the charts, and ignores responses that arrive after a
 * newer request has started.
 *
 * @example
 * const perf = useSupplierPerformance();
 * perf.setFilters({ ...perf.filters, division: '42' });
 *
 * @returns data, loading, error, filters, setFilters, resetFilters, reload
 */
export function useSupplierPerformance() {
  const [filters, setFilters] = useState<PerformanceFilterState>(DEFAULT_PERFORMANCE_FILTERS);
  const [data, setData] = useState<SupplierPerformanceDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const bounds = useRef<{ latest: string | null; first: string | null }>({ latest: null, first: null });
  const requestId = useRef(0);
  const { getViewQueryParams, viewKey } = useView();
  const lastViewKey = useRef(viewKey);

  useEffect(() => {
    if (lastViewKey.current === viewKey) return;
    lastViewKey.current = viewKey;
    // A division picked on the page may sit outside the newly switched view.
    setFilters((f) => (f.division ? { ...f, division: '' } : f));
  }, [viewKey]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    supplierPerformanceAPI
      .getDashboard({ ...toParams(filters, bounds.current.latest, bounds.current.first), ...viewParams(getViewQueryParams()) })
      .then((res) => {
        if (id !== requestId.current) return;
        setData(res.data);
        if (res.data.range) {
          bounds.current = { latest: res.data.range.latest_complete, first: res.data.range.first_data };
        }
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        console.error('Error loading supplier performance:', err);
        setError(getApiErrorMessage(err, 'Failed to load supplier performance'));
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
    // viewKey changes whenever getViewQueryParams does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, reloadKey, viewKey]);

  const resetFilters = useCallback(() => setFilters(DEFAULT_PERFORMANCE_FILTERS), []);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, filters, setFilters, resetFilters, reload };
}
